import { Link } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { bs } from "date-fns/locale";
import { User, ShieldBan, PackageX, Ban, Undo2, RotateCcw } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatCurrency, formatDate, plural } from "@/lib/utils";
import { RETURN_REASON_LABEL } from "@/lib/package-return-labels";
import { statusDotClass, statusLabel } from "@/components/orders/status-badge";
import type { CustomerContext } from "@/hooks/use-customer-context";

type Props = {
  name: string;
  email?: string;
  phone?: string;
  isBanned: boolean;
  banReason?: string;
  context?: CustomerContext;
  isLoading: boolean;
};

/** Sitni naslov iznad broja — isti jezik kao zaglavlja tabele narudžbi. */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{children}</span>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="px-3 py-2.5 min-w-0">
      <Eyebrow>{label}</Eyebrow>
      <div className="mt-0.5 text-sm font-medium tabular-nums truncate">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground truncate">{hint}</div>}
    </div>
  );
}

/** Rizik red: crvena okomita crtica lijevo, isti motiv kao status u listi narudžbi. */
function RiskLine({
  icon: Icon,
  text,
  detail,
  to,
}: {
  icon: LucideIcon;
  text: string;
  detail?: string;
  to?: string;
}) {
  const body = (
    <div className="relative flex items-start gap-1.5 py-1.5 pl-3 pr-3">
      <span aria-hidden className="absolute left-0 top-2.5 h-3.5 w-[2px] bg-red-500" />
      <Icon className="h-3.5 w-3.5 shrink-0 text-red-600 mt-px" />
      <div className="min-w-0">
        <div className="text-xs text-red-700 dark:text-red-400">{text}</div>
        {detail && <div className="text-[10px] text-red-700/70 dark:text-red-400/70">{detail}</div>}
      </div>
    </div>
  );
  return to ? (
    <Link to={to} className="block hover:bg-red-500/5 transition-colors">
      {body}
    </Link>
  ) : (
    body
  );
}

function relative(date: string | null): string | undefined {
  if (!date) return undefined;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return undefined;
  return formatDistanceToNow(d, { addSuffix: true, locale: bs });
}

export function CustomerCard({ name, email, phone, isBanned, banReason, context, isLoading }: Props) {
  const ctx = context;
  const risks = ctx
    ? {
        packages: ctx.packageReturns.count,
        cancelled: ctx.cancelledCount,
        refunded: ctx.refundedCount,
        returns: ctx.returnRequests.count,
      }
    : null;
  const hasRisk =
    !!risks && (risks.packages > 0 || risks.cancelled > 0 || risks.refunded > 0 || risks.returns > 0);

  return (
    <div className="ring-1 ring-foreground/10">
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b">
        <div className="flex items-center gap-1.5">
          <User className="h-3 w-3 text-muted-foreground" />
          <span className="text-xs font-medium">Kupac</span>
        </div>
      </div>

      <div className="p-3 text-xs">
        <p className="font-medium">{name || "Nepoznat kupac"}</p>
        {email && <p className="text-muted-foreground mt-0.5 break-all">{email}</p>}
        {phone && <p className="text-muted-foreground mt-0.5 tabular-nums">{phone}</p>}
      </div>

      {isBanned && (
        <div className="border-t border-red-500/20">
          <RiskLine icon={ShieldBan} text="Kupac je banovan" detail={banReason || undefined} />
        </div>
      )}

      {isLoading && (
        <div className="border-t p-3 flex flex-col gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {/* Kupac s jednom narudžbom: potrošeno, prosjek i zadnja narudžba bi bili
          isti podatak u četiri okvira — umjesto toga jedan red koji to i kaže. */}
      {ctx && ctx.ordersCount === 1 && (
        <div className="border-t px-3 py-2.5">
          <Eyebrow>Historija</Eyebrow>
          <div className="mt-0.5 text-xs">Prvi put kupuje kod nas.</div>
          <div className="text-[10px] text-muted-foreground tabular-nums">
            {formatCurrency(ctx.lifetimeValue, ctx.currency)} · {relative(ctx.firstOrderAt) ?? "—"}
          </div>
        </div>
      )}

      {ctx && ctx.ordersCount > 1 && (
        <>
          {/* Brojevi u mreži s hairline linijama umjesto razmaka — gušće se čita
              i drži isti ritam kao tabela narudžbi. */}
          <div className="grid grid-cols-2 border-t divide-x divide-y divide-foreground/10 [&>*]:border-0">
            <Stat
              label="Potrošeno"
              value={formatCurrency(ctx.lifetimeValue, ctx.currency)}
              hint={
                ctx.ordersCount > 1
                  ? `kroz ${ctx.ordersCount} ${plural(ctx.ordersCount, "narudžbu", "narudžbe", "narudžbi")}`
                  : undefined
              }
            />
            <Stat
              label="Prosjek"
              value={ctx.averageOrderValue > 0 ? formatCurrency(ctx.averageOrderValue, ctx.currency) : "—"}
              hint="po narudžbi"
            />
            <Stat
              label="Kupac od"
              value={ctx.firstOrderAt ? formatDate(ctx.firstOrderAt) : "—"}
              hint={relative(ctx.firstOrderAt)}
            />
            <Stat
              label="Zadnja narudžba"
              value={ctx.lastOrderAt ? formatDate(ctx.lastOrderAt) : "—"}
              hint={relative(ctx.lastOrderAt)}
            />
          </div>

          {hasRisk && (
            <div className="border-t border-red-500/20 bg-red-500/5 divide-y divide-red-500/10">
              {risks!.packages > 0 && (
                <RiskLine
                  icon={PackageX}
                  text={`${risks!.packages} ${plural(risks!.packages, "odbijena pošiljka", "odbijene pošiljke", "odbijenih pošiljki")}`}
                  detail={ctx.packageReturns.reasons
                    .map((r) => RETURN_REASON_LABEL[r] ?? r)
                    .join(" · ")}
                  to="/fulfillment/returned"
                />
              )}
              {risks!.cancelled > 0 && (
                <RiskLine
                  icon={Ban}
                  text={`${risks!.cancelled} ${plural(risks!.cancelled, "otkazana narudžba", "otkazane narudžbe", "otkazanih narudžbi")}`}
                />
              )}
              {risks!.refunded > 0 && (
                <RiskLine
                  icon={RotateCcw}
                  text={`${risks!.refunded} ${plural(risks!.refunded, "refundirana narudžba", "refundirane narudžbe", "refundiranih narudžbi")}`}
                />
              )}
              {risks!.returns > 0 && (
                <RiskLine
                  icon={Undo2}
                  text={`${risks!.returns} ${plural(risks!.returns, "zahtjev", "zahtjeva", "zahtjeva")} za povrat`}
                  to="/returns"
                />
              )}
            </div>
          )}

          <div className="border-t">
            <div className="px-3 pt-2.5 pb-1">
              <Eyebrow>Ranije narudžbe</Eyebrow>
            </div>
            {ctx.recentOrders.length === 0 ? (
              <p className="px-3 pb-3 text-xs text-muted-foreground">Nema ranijih narudžbi.</p>
            ) : (
              <div className="pb-1">
                {ctx.recentOrders.map((o) => (
                  <Link
                    key={o.wcOrderId}
                    to="/orders/$orderId"
                    params={{ orderId: String(o.wcOrderId) }}
                    className="relative flex items-center gap-2 py-1.5 pl-3 pr-3 hover:bg-muted/40 transition-colors"
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "absolute left-0 top-1/2 h-3.5 w-[2px] -translate-y-1/2",
                        statusDotClass(o.status),
                      )}
                    />
                    <span className="text-xs font-medium tabular-nums">#{o.number}</span>
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      {o.date ? formatDate(o.date) : "—"}
                    </span>
                    <span className="text-[10px] text-muted-foreground truncate">
                      {statusLabel(o.status)}
                    </span>
                    <span className="ml-auto text-xs tabular-nums">
                      {formatCurrency(o.total, ctx.currency)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
